import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle2, Phone, Bot, Calendar, Bell, ChevronLeft, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { MobileActivationJourney } from '../components/activation/MobileActivationJourney';
import { getSubscriptionPlan, DEFAULT_SUBSCRIPTION_PLAN_ID } from '../data/subscriptionPlans';
import type { JourneyProgress, JourneyStart } from '../types/forwarding';
import { useTheme } from '../contexts/ThemeContext';
import { ProCheckoutScreen } from './ProCheckoutScreen';
interface IosActivationFlowProps {
  onComplete: (progress: JourneyProgress, destination: 'home' | 'callerExperience') => void;
  /** Reported on every step so an interrupted setup can be resumed. */
  onProgress?: (progress: JourneyProgress) => void;
  onCancel: () => void;
  onBack: () => void;
  /**
   * Opens the shared support screen (same destination as the Account tab's
   * "Contact Support" entry).
   */
  onContactSupport?: () => void;
  /**
   * Returning user: resume or reactivate their saved number, carrier, rules
   * and progress. Skips the trial/checkout intro.
   */
  journeyStart?: JourneyStart;
  /** Subscription was completed immediately before this flow. */
  subscriptionActive?: boolean;
  /** Routes an in-flow subscription through the shared forwarding-number choice. */
  onSubscriptionSuccess?: () => void;
}
export function IosActivationFlow({
  onComplete,
  onBack,
  onContactSupport,
  onProgress,
  journeyStart,
  subscriptionActive = false,
  onSubscriptionSuccess
}: IosActivationFlowProps) {
  const {
    isLight
  } = useTheme();
  // A freshly completed subscription (or a returning user) skips the
  // introduction and checkout and goes straight to the forwarding setup.
  const [step, setStep] = useState(journeyStart || subscriptionActive ? 4 : 1);
  const renderStep = () => {
    switch (step) {
      case 1:
        return <motion.div key="step1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-2386" data-mp="1n2ftd9w9cl2f">
            <button onClick={onBack} className={`absolute top-6 left-6 p-2 rounded-full z-10 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-2387" data-mp="1wz0z5xg3q07b">
              <ChevronLeft className="w-5 h-5" data-id="element-2388" data-mp="25snrtep0inca" />
            </button>

            <div className="mt-12 mb-8 text-center" data-id="element-2389" data-mp="2aqywlb0ga7nn">
              <div className={`w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/20'}`} data-id="element-2390" data-mp="2fr3hrecazqk7">
                <Bot className={`w-8 h-8 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2391" data-mp="2kpep9if4loz9" />
              </div>
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2392" data-mp="lf5avj1gv6rh">
                Activate Your AI Assistant
              </h1>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2393" data-mp="33n88k7oapq3e">
                Start your 3-day free trial and let your AI answer calls, take
                messages, schedule meetings, and notify you instantly.
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-2394" data-mp="2pw34mtdwabn9">
              {[{
              icon: Phone,
              text: 'AI answers missed calls 24/7'
            }, {
              icon: Bell,
              text: 'Smart call summaries'
            }, {
              icon: Calendar,
              text: 'Meeting scheduling'
            }, {
              icon: Zap,
              text: 'Personalized AI responses'
            }, {
              icon: CheckCircle2,
              text: 'Monthly AI minutes included'
            }].map((benefit, i) => <div key={i} className="flex items-center gap-3" data-id="element-2395" data-mp="38nm4wsgjbbll" data-mpi={i}>
                  <div className={`p-1.5 rounded-full ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/20 text-vexu'}`} data-id="element-2396" data-mp="2268qm8huah1f">
                    <benefit.icon className="w-4 h-4" data-id="element-2397" data-mp="3l3ao88yvb0lb" />
                  </div>
                  <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2398" data-mp="3l17ubdulgnbb">
                    {benefit.text}
                  </span>
                </div>)}
            </div>

            <div className={`p-4 rounded-2xl mb-8 ${isLight ? 'bg-slate-50 border border-slate-200' : 'bg-white/5 border border-white/10'}`} data-id="element-2399" data-mp="3h0xlzujbxkta">
              <div className="flex items-center gap-2 mb-2" data-id="element-2400" data-mp="8jt2vj7ah7mp">
                <Shield className={`w-4 h-4 ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2401" data-mp="clxvcem03y7r" />
                <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2402" data-mp="3us2dg9cjn6sj">
                  Compatibility Guarantee
                </h3>
              </div>
              <p className={`text-xs leading-relaxed mb-2 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2403" data-mp="2jdfe0lxhsmqc">
                Vexu works with supported mobile carriers that offer call
                forwarding.
              </p>
              <p className={`text-xs leading-relaxed mb-2 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2404" data-mp="2grphbo61csr4">
                If your carrier or mobile plan isn't compatible, you can cancel
                your subscription during setup before completing activation.
              </p>
              <p className={`text-xs leading-relaxed font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2405" data-mp="2kvx6tnkbicqe">
                We'll guide you through every step and verify that your
                activation was successful.
              </p>
            </div>

            <div className="mt-auto" data-id="element-2406" data-mp="26ut9lknyoanm">
              <Button fullWidth onClick={() => setStep(2)} data-id="element-2407" data-mp="26vuushyoydj2">
                Start 3-Day Free Trial
              </Button>
            </div>
          </motion.div>;
      case 2:
        return <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="absolute inset-0 z-50 bg-white dark:bg-black" data-id="element-2408" data-mp="1edh3z2brvrth">
            <ProCheckoutScreen plan={getSubscriptionPlan(DEFAULT_SUBSCRIPTION_PLAN_ID)} onBack={() => setStep(1)} onSuccess={() => {
            if (onSubscriptionSuccess) {
              onSubscriptionSuccess();
              return;
            }
            setStep(3);
          }} data-id="element-2409" data-mp="3egruh9yht91j" />
          </motion.div>;
      case 3:
        return <motion.div key="step3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col items-center justify-center p-6 text-center" data-id="element-2410" data-mp="2vj8flppcsw77">
            <div className={`w-20 h-20 rounded-full mb-6 flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/20'}`} data-id="element-2411" data-mp="frkl00xtbfrk">
              <span className="text-4xl" data-id="element-2412" data-mp="jupljxyc17dk">🎉</span>
            </div>
            <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2413" data-mp="249dpuriydkx5">
              Welcome to Vexu Pro
            </h1>
            <p className={`text-sm leading-relaxed mb-8 max-w-[260px] ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2414" data-mp="1viddbyb00lm0">
              Your AI assistant is now ready. Let's connect your phone so it can
              answer your calls whenever you're unavailable.
            </p>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full mb-12 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-2415" data-mp="1li31oi4m3m8y">
              <span className="text-lg" data-id="element-2416" data-mp="1cjre6oz1l4kq">⏱</span>
              <span className="text-xs font-medium" data-id="element-2417" data-mp="17kxyqr0733hx">Estimated setup time: About 1 minute</span>
            </div>
            <Button fullWidth onClick={() => setStep(4)} data-id="element-2418" data-mp="36xscn8qojpka">
              Connect My Phone
            </Button>
          </motion.div>;
      case 4:
        return <motion.div key="step4" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="absolute inset-0" data-id="element-2419" data-mp="2at3e23nzklcq">
            <MobileActivationJourney start={journeyStart} onExit={onBack} onContactSupport={onContactSupport} onProgress={onProgress} onComplete={onComplete} data-id="element-2420" data-mp="2141te06hxfsf" />
          </motion.div>;
    }
  };
  return <div className={`h-full w-full relative overflow-hidden ${isLight ? 'bg-stone-100' : 'bg-black'}`} data-id="element-2421" data-mp="1w4p12zwuvgig">
      <AnimatePresence mode="wait" data-id="element-2422" data-mp="1i57i5168qxh0">{renderStep()}</AnimatePresence>
    </div>;
}